import { useEffect, useRef } from "react";
import { useMutation } from "@tanstack/react-query";
import { Capacitor } from "@capacitor/core";
import { TRPCClientError } from "@trpc/client";

import { useTRPC } from "@/trpc";
import { isImpersonating } from "@/features/impersonation/impersonation-session";
import { isTimezone } from "@/features/user/enums";
import { Platform } from "@api/shared-runtime/enums";
import type { TimeZone } from "@api/shared-runtime/timezone";

const TIMEZONE_SYNC_INTERVAL_MS = 30 * 60 * 1000;

/**
 * A long-lived tab can outlive its session (see the idle-expiry handling in
 * ajax/clients/supabase), after which every tick 401s forever — 685 such events
 * in 30 days from ~40 users. Treat any auth failure as terminal for the poll
 * rather than retrying on a schedule. See Sentry SAILE-WEB-ER.
 *
 * Keyed on the error code rather than the message so an expired token and a
 * missing one are handled alike, matching how the route loaders test for this.
 */
const UNAUTHORIZED_CODE = "UNAUTHORIZED";

const isUnauthenticatedError = (error: unknown) =>
  error instanceof TRPCClientError && error.data?.code === UNAUTHORIZED_CODE;

/**
 * Mirrors the browser's timezone onto the user record on mount and every 30
 * minutes, so server-rendered dates and notification timing follow the user.
 *
 * Stops polling once the session is gone: continuing would neither succeed nor
 * inform the user, and a signed-out user has no record to update. Sign-in
 * remounts the route and starts a fresh interval.
 */
export const useTimezoneSync = (isAuthenticated: boolean) => {
  const trpc = useTRPC();
  const { mutate: updateTimezone } = useMutation(
    trpc.user.updateTimezone.mutationOptions(),
  );

  // Held in refs, not state, so stopping the poll does not re-run the effect and
  // immediately recreate the interval it just cleared. The interval id lives in a
  // ref rather than a local so `onError` can clear it without reading a `const`
  // that is still in its temporal dead zone during the first synchronous call.
  const isStoppedRef = useRef(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    // An impersonating staff member's browser timezone is not the user's.
    if (!isAuthenticated || isImpersonating()) {
      return;
    }

    isStoppedRef.current = false;

    const stopPolling = () => {
      isStoppedRef.current = true;
      if (intervalRef.current !== null) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };

    const syncTimezone = () => {
      if (isStoppedRef.current) {
        return;
      }

      const timeZone = new Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (!isTimezone(timeZone)) {
        console.log(
          "Timezone is not a valid US timezone. Defaulting to New York.",
          timeZone,
        );
        return;
      }

      updateTimezone(
        {
          timeZone: timeZone as TimeZone,
          platform: Capacitor.getPlatform() as Platform,
        },
        {
          onError: (error) => {
            if (isUnauthenticatedError(error)) {
              stopPolling();
              return;
            }
            console.error("Failed to update timezone:", error);
          },
        },
      );
    };

    syncTimezone();
    intervalRef.current = setInterval(syncTimezone, TIMEZONE_SYNC_INTERVAL_MS);

    return stopPolling;
  }, [isAuthenticated, updateTimezone]);
};
